import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  Plus,
  Search,
  Filter,
  Download,
  Upload,
  Edit,
  Trash2,
  Eye,
  Calendar,
  User,
  Building,
  FileText,
  XCircle,
  MoreVertical,
  CheckCircle,
  ChevronDown,
  File,
  FileSpreadsheet,
  History,
} from "lucide-react";
import * as XLSX from "xlsx";
import { useAuth } from "../../contexts/AuthContext";
import { complianceService, plantService, userService } from "../../services/api";
import {
  getStatusBadge,
  formatDate,
  extractArrayData,
} from "../../utils/helpers";
import LoadingSpinner from "../Common/LoadingSpinner";
import ImportModal from "../Import_Export/ImportModal";
import Tooltip from "../../utils/Tooltip";
import SubmitVerificationModal from "./SubmitVerificationModal";
import VerifyModal from "./VerifyModal";
import HistoryModal from "./HistoryModal";
import ConfirmModal from "../Common/ConfirmModal";

// Load jsPDF and jspdf-autotable via UMD scripts
const loadJsPDFScripts = () => {
  return new Promise((resolve, reject) => {
    // Load jsPDF UMD
    const jspdfScript = document.createElement("script");
    jspdfScript.src = "https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js";
    jspdfScript.async = true;
    jspdfScript.onload = () => {
      // Load jspdf-autotable after jsPDF
      const autotableScript = document.createElement("script");
      autotableScript.src = "https://cdn.jsdelivr.net/npm/jspdf-autotable@3.8.2/dist/jspdf.plugin.autotable.min.js";
      autotableScript.async = true;
      autotableScript.onload = () => resolve();
      autotableScript.onerror = () => reject(new Error("Failed to load jspdf-autotable"));
      document.head.appendChild(autotableScript);
    };
    jspdfScript.onerror = () => reject(new Error("Failed to load jsPDF"));
    document.head.appendChild(jspdfScript);
  });
};

const ComplianceList = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // State for filters, pagination, and selection
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    frequency: "",
    assignedTo: "",
    plant: "",
    dueTill: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [selectedCompliances, setSelectedCompliances] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showVerificationPendingOnly, setShowVerificationPendingOnly] = useState(false);
  const [showSentForVerificationOnly, setShowSentForVerificationOnly] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [isExporting, setIsExporting] = useState(false);


  const [openDropdown, setOpenDropdown] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [activeCompliance, setActiveCompliance] = useState(null);
  const [showConfirmDeleteSelected, setShowConfirmDeleteSelected] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      setOpenDropdown(null);
      // Close export dropdown if clicking outside
      if (showExportDropdown && !event.target.closest('.export-dropdown-container')) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showExportDropdown]);




  // Fetch compliances without filters
  const {
    data: compliancesData,
    isLoading,
    error,
  } = useQuery(
    ["compliances", currentPage, pageSize, filters, showSentForVerificationOnly],
    () => complianceService.getCompliances({
      page: currentPage,
      limit: pageSize,
      search: filters.search,
      status: filters.status,
      frequency: filters.frequency,
      assignedTo: filters.assignedTo,
      plant: filters.plant,
      dueTill: filters.dueTill,
      awaitingVerification: showSentForVerificationOnly
    }),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  // Fetch plants for filter dropdown
  const { data: plantsData } = useQuery(
    ["plants"],
    () => plantService.getPlants(),
    {
      staleTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  // Fetch users for filter dropdown
  const { data: usersData } = useQuery(
    ["users"],
    () => userService.getUsers(),
    {
      staleTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  const plants = extractArrayData(plantsData) || [];
  const users = extractArrayData(usersData) || [];
  const compliances = extractArrayData(compliancesData) || [];

  // Frontend filtering logic
  const filteredCompliances = compliances.filter((compliance) => {
    let matches = true;

    if (showVerificationPendingOnly) {
      matches = matches && compliance.verificationStatus === 'pending';
    }

    return matches;
  });

  // We now use server-side pagination, so we don't need to slice locally.
  // The 'compliances' array already contains just the items for the current page.
  const totalFiltered = compliancesData?.pagination?.total || filteredCompliances.length;
  const totalPages = compliancesData?.pagination?.pages || Math.ceil(totalFiltered / pageSize);
  const paginatedCompliances = filteredCompliances; // The backend already sliced this!

  // Handle select all checkbox
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCompliances([]);
    } else {
      setSelectedCompliances(paginatedCompliances.map((c) => c._id));
    }
    setSelectAll(!selectAll);
  };

  // Handle individual checkbox
  const handleSelectCompliance = (id) => {
    setSelectedCompliances((prev) =>
      prev.includes(id)
        ? prev.filter((cId) => cId !== id)
        : [...prev, id]
    );
    setSelectAll(false);
  };

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation(
    (ids) => Promise.all(ids.map((id) => complianceService.deleteCompliance(id))),
    {
      onSuccess: () => {
        toast.success("Selected compliances deleted successfully");
        queryClient.invalidateQueries("compliances");
        setSelectedCompliances([]);
        setSelectAll(false);
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.message || "Failed to delete compliances"
        );
      },
    }
  );

  const deleteMutation = useMutation(
    (id) => complianceService.deleteCompliance(id),
    {
      onSuccess: () => {
        toast.success("Compliance deleted successfully");
        queryClient.invalidateQueries("compliances");
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.message || "Failed to delete compliance"
        );
      },
    }
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
    setSelectedCompliances([]);
    setSelectAll(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setSelectedCompliances([]);
    setSelectAll(false);
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      status: "",
      frequency: "",
      assignedTo: "",
      plant: "",
      dueTill: "",
    });
    setCurrentPage(1);
    setSelectedCompliances([]);
    setSelectAll(false);
  };

  const openSubmitForVerification = (compliance) => {
    setActiveCompliance(compliance);
    setShowSubmitModal(true);
  };

  const openVerify = (compliance) => {
    setActiveCompliance(compliance);
    setShowVerifyModal(true);
  };

  const openHistory = (compliance) => {
    setActiveCompliance(compliance);
    setShowHistoryModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this compliance?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleBulkDelete = () => {
    if (selectedCompliances.length === 0) return;
    setShowConfirmDeleteSelected(true);
  };

  const confirmBulkDelete = () => {
    bulkDeleteMutation.mutate(selectedCompliances);
  };

  const handleImport = () => {
    setShowImportModal(true);
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    setShowExportDropdown(false);
    try {
      const response = await complianceService.exportCompliances({
        ...filters,
        awaitingVerification: showSentForVerificationOnly
      });
      const allCompliances = response.data || [];

      if (allCompliances.length === 0) {
        toast.error("No data to export");
        return;
      }

      await loadJsPDFScripts();
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text("Compliance Report", 14, 20);

      const columns = [
        { header: "Sr. No", dataKey: "srNo" },
        { header: "Title", dataKey: "title" },
        { header: "Status", dataKey: "status" },
        { header: "Due Date", dataKey: "dueDate" },
        { header: "Next Due On", dataKey: "nextDueDate" },
        { header: "Assigned To", dataKey: "assignedTo" },
        { header: "Plant", dataKey: "plant" },
        { header: "Frequency", dataKey: "frequency" },
      ];

      const data = allCompliances.map((compliance, index) => ({
        srNo: index + 1,
        title: compliance.title || "N/A",
        status: compliance.status || "N/A",
        dueDate: formatDate(compliance.dueDate) || "N/A",
        nextDueDate: formatDate(compliance.nextDueDate) || "N/A",
        assignedTo:
          Array.isArray(compliance.assignedTo) && compliance.assignedTo.length > 0
            ? compliance.assignedTo.map((user) => user.name || "Unknown").join(", ")
            : "Unassigned",
        plant: compliance.plant?.name || "N/A",
        frequency: compliance.frequency || "N/A",
      }));

      doc.autoTable({
        columns,
        body: data,
        startY: 30,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [59, 130, 246], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        margin: { top: 30 },
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(
          `Generated on ${new Date().toLocaleString()}`,
          14,
          doc.internal.pageSize.height - 10
        );
        doc.text(
          `Page ${i} of ${pageCount}`,
          doc.internal.pageSize.width - 30,
          doc.internal.pageSize.height - 10
        );
      }

      doc.save("compliance_report.pdf");
      toast.success("Compliance report exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export compliances as PDF");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    setShowExportDropdown(false);
    try {
      const response = await complianceService.exportCompliances({
        ...filters,
        awaitingVerification: showSentForVerificationOnly
      });
      const allCompliances = response.data || [];

      // Check if there's data to export
      if (allCompliances.length === 0) {
        toast.error("No data to export");
        return;
      }

      // Prepare data for Excel export
      const excelData = allCompliances.map((compliance, index) => {
        // Handle plants - could be single plant or array of plants
        let plantName = "N/A";
        if (Array.isArray(compliance.plants) && compliance.plants.length > 0) {
          plantName = compliance.plants.map((p) => (p.name || p)).join(", ");
        } else if (compliance.plant?.name) {
          plantName = compliance.plant.name;
        } else if (compliance.plant) {
          plantName = compliance.plant;
        }

        // Handle assigned users
        const assignedTo = Array.isArray(compliance.assignedTo) && compliance.assignedTo.length > 0
          ? compliance.assignedTo.map((user) => user.name || user.email || "Unknown").join(", ")
          : "Unassigned";

        return {
          "Sr. No": index + 1,
          "Title": compliance.title || "N/A",
          "Status": compliance.status || "N/A",
          "Frequency": compliance.frequency || "N/A",
          "License": compliance.license || "N/A",
          "Due Date": compliance.dueDate ? formatDate(compliance.dueDate) : "N/A",
          "Next Due On": compliance.nextDueDate ? formatDate(compliance.nextDueDate) : "N/A",
          "Assigned To": assignedTo,
          "Plant": plantName,
          "Remark": compliance.remark || "N/A",
          "Reason": compliance.reason || "N/A",
          "Attached File": compliance.cloudinaryURL || "N/A",
          "Created At": compliance.createdAt ? formatDate(compliance.createdAt) : "N/A",
        };
      });

      // Create a new workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 8 },   // Sr. No
        { wch: 30 },  // Title
        { wch: 12 },  // Status
        { wch: 12 },  // Frequency
        { wch: 20 },  // License
        { wch: 15 },  // Due Date
        { wch: 15 },  // Next Due On
        { wch: 25 },  // Assigned To
        { wch: 20 },  // Plant
        { wch: 30 },  // Remark
        { wch: 30 },  // Reason
        { wch: 40 },  // Attached File
        { wch: 20 },  // Created At
      ];
      ws['!cols'] = colWidths;

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, "Compliances");

      // Generate filename with timestamp
      const filename = `compliance_report_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Write the file
      XLSX.writeFile(wb, filename);
      toast.success("Compliance report exported to Excel successfully");
    } catch (error) {
      console.error("Excel export error:", error);
      toast.error("Failed to export compliances to Excel");
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportSubmit = async (file) => {
    setIsImporting(true);
    try {
      await complianceService.importCompliances(file);
      toast.success("Compliances imported successfully");
      queryClient.invalidateQueries("compliances");
      setShowImportModal(false);
    } catch (error) {
      console.error("Import error details:", error.response?.data);
      const backendErrors = error.response?.data?.errors;
      if (Array.isArray(backendErrors) && backendErrors.length > 0) {
        // Show first few errors to avoid overwhelming the screen
        const displayErrors = backendErrors.slice(0, 3).join("\n");
        const remainingCount = backendErrors.length - 3;
        toast.error(
          `Import Failed:\n${displayErrors}${remainingCount > 0 ? `\n...and ${remainingCount} more errors` : ""}`,
          { duration: 5000, icon: null }
        );
      } else {
        toast.error(
          error.response?.data?.message || "Failed to import compliances. Please verify the template",
          { duration: 5000, icon: null }
        );
      }
    } finally {
      setIsImporting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <XCircle className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Error loading compliances
          </h3>
          <p className="mt-1 text-sm text-gray-500">{error.message}</p>
        </div>
      </div>
    );
  }

  const renderStatusBadge = (compliance) => {
    if (compliance.verificationStatus === 'rejected') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Rejected
        </span>
      );
    }
    return getStatusBadge(compliance.status);
  };

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-500">Compliances</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage and track compliance requirements
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-2">
            {user?.role === "admin" && (
              <>
                <button
                  onClick={handleImport}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Import
                </button>
                <div
                  className="relative export-dropdown-container"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowExportDropdown(!showExportDropdown);
                    }}
                    disabled={isExporting}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Export
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </button>
                  {showExportDropdown && (
                    <div
                      className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="py-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExportPDF();
                          }}
                          disabled={isExporting}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          <File className="h-4 w-4 mr-2" />
                          Export as PDF
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExportExcel();
                          }}
                          disabled={isExporting}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          <FileSpreadsheet className="h-4 w-4 mr-2" />
                          Export as Excel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {selectedCompliances.length > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Selected ({selectedCompliances.length})
                  </button>
                )}
              </>
            )}
            {(user?.role === 'admin' || user?.role === 'superAdmin') && (
              <div className="flex items-center gap-4">
                {/* <label className="inline-flex items-center text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={showVerificationPendingOnly}
                  onChange={(e) => setShowVerificationPendingOnly(e.target.checked)}
                />
                Show only pending verifications
              </label> */}
                <label className="inline-flex items-center text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={showSentForVerificationOnly}
                    onChange={(e) => {
                      setShowSentForVerificationOnly(e.target.checked);
                      setCurrentPage(1);
                    }}
                  />
                  Awaiting Verification
                </label>
              </div>
            )}
            {user?.role === "admin" && (
              <Link
                to="/compliances/create"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Compliance
              </Link>
            )}
          </div>
        </div>



        <div className="bg-white shadow rounded-lg">
          <div className="p-6">
            <form onSubmit={handleSearch} className="mb-4">
              <div className="flex gap-2">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search compliances..."
                      value={filters.search}
                      onChange={(e) =>
                        handleFilterChange("search", e.target.value)
                      }
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowFilters(!showFilters)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                </button>
              </div>
            </form>

            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange("status", e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="overdue">Overdue</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Frequency
                  </label>
                  <select
                    value={filters.frequency}
                    onChange={(e) =>
                      handleFilterChange("frequency", e.target.value)
                    }
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Frequencies</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="6 month">6 Month</option>
                    <option value="yearly">Yearly</option>
                    <option value="2 year">2 Year</option>
                    <option value="3 year">3 Year</option>
                    <option value="4 year">4 Year</option>
                    <option value="5 year">5 Year</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assigned To
                  </label>
                  <select
                    value={filters.assignedTo}
                    onChange={(e) =>
                      handleFilterChange("assignedTo", e.target.value)
                    }
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Users</option>
                    {users.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>

                {(user?.role === "admin" || user?.role === "superAdmin") && (<div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plant
                  </label>
                  <select
                    value={filters.plant}
                    onChange={(e) => handleFilterChange("plant", e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Plants</option>
                    {plants.map((plant) => (
                      <option key={plant._id} value={plant._id}>
                        {plant.name}
                      </option>
                    ))}
                  </select>
                </div>)}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Till
                  </label>
                  <input
                    type="date"
                    value={filters.dueTill}
                    onChange={(e) =>
                      handleFilterChange("dueTill", e.target.value)
                    }
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            )}

            {showFilters && (
              <div className="flex justify-end">
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white shadow rounded-lg">
          {isLoading ? (
            <div className="p-6">
              <LoadingSpinner text="Loading compliances..." />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto relative">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {user?.role === "admin" && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sr. No
                      </th>
                      <th className="px-6 py-3 px-14 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Due Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Next Due On
                      </th>
                      <th className="px-6 py-3  text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assigned To
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Frequency
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedCompliances.length > 0 ? (
                      paginatedCompliances.map((compliance, index) => (
                        <tr key={compliance._id} className="">
                          {user?.role === "admin" && (
                            <td className="px-6 py-4 whitespace-nowrap sticky left-0 bg-white z-10">
                              <input
                                type="checkbox"
                                checked={selectedCompliances.includes(compliance._id)}
                                onChange={() => handleSelectCompliance(compliance._id)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap">
                            {(currentPage - 1) * pageSize + index + 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <FileText className="h-5 w-5 text-gray-400 mr-3" />
                              <div>
                                <div className="relative group max-w-[150px]">
                                  <div className="truncate text-sm font-medium text-gray-500">
                                    {compliance.title || "N/A"}
                                  </div>
                                  <div className="absolute left-0 top-full mt-2 hidden group-hover:flex z-50 w-auto bg-gray-900 text-white text-xs rounded px-2 py-1 shadow-lg whitespace-nowrap">
                                    {compliance.title || "N/A"}
                                  </div>
                                </div>
                                {/* <div className="text-sm text-gray-500">
                                  {compliance.license || "No description"}
                                </div> */}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {renderStatusBadge(compliance)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-900">
                                {formatDate(compliance.dueDate) || "N/A"}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-900">
                                {formatDate(compliance.nextDueDate) || "N/A"}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <User className="h-4 w-4 text-gray-400 mr-2" />
                              <Tooltip
                                content={
                                  Array.isArray(compliance.assignedTo) && compliance.assignedTo.length > 0
                                    ? compliance.assignedTo.map((user) => user.name || "Unknown").join(", ")
                                    : "Unassigned"
                                }
                                maxWidth="150px"
                                truncateLength={15}
                              >
                                {Array.isArray(compliance.assignedTo) && compliance.assignedTo.length > 0
                                  ? compliance.assignedTo.map((user) => user.name || "Unknown").join(", ")
                                  : "Unassigned"}
                              </Tooltip>
                            </div>
                          </td>




                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Building className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-900">
                                {compliance.plant?.name || "N/A"}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {compliance.frequency || "N/A"}
                            </span>
                          </td>



                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium sticky right-0 bg-white ${openDropdown === compliance._id ? 'z-20' : 'z-10'}`}>
                            <div className="relative">
                              <button
                                className="flex items-center justify-center p-1 rounded hover:          "
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenDropdown(openDropdown === compliance._id ? null : compliance._id);
                                }}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </button>

                              {openDropdown === compliance._id && (
                                <div className="absolute right-0 mt-2  w-48  bg-white rounded-md  z-50 border border-gray-200  text-gray-600 ">   <div className="py-1">
                                  <Link
                                    to={`/compliances/${compliance._id}`}
                                    className="block rounded px-2 py-2 "
                                  >
                                    <div className="flex items-center hover:bg-blue-100 rounded px-2 py-1">
                                      <Eye className="h-4 w-4 mr-2" />
                                      View
                                    </div>
                                  </Link>

                                  {user?.role === "admin" && (
                                    <Link
                                      to={`/compliances/${compliance._id}/edit`}
                                      className="block rounded px-2 py-2"
                                    >
                                      <div className="flex items-center text-gray-600  hover:bg-blue-100 rounded px-2 py-1">
                                        <Edit className="h-4 w-4 mr-2" />
                                        Edit
                                      </div>
                                    </Link>
                                  )}

                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openHistory(compliance);
                                      setOpenDropdown(null);
                                    }}
                                    className="w-full text-left block rounded px-2 py-2 text-gray-600"
                                  >
                                    <div className="flex items-center hover:bg-blue-100 rounded px-2 py-1">
                                      <History className="h-4 w-4 mr-2" />
                                      History
                                    </div>
                                  </button>

                                  {user?.role === "employee" &&
                                    compliance.assignedTo?.some((u) => u._id === user._id) && compliance.verificationStatus === 'pending' && !compliance.isSendForVerification && (
                                      <button
                                        onClick={() => openSubmitForVerification(compliance)}
                                        className="w-full text-left block rounded px-2 py-2  text-gray-600 "
                                      >
                                        <div className="flex items-center hover:bg-blue-100 rounded px-2 py-1">
                                          <Upload className="h-4 w-4 mr-2" />
                                          Send for verification
                                        </div>
                                      </button>
                                    )}

                                  {(user?.role === "admin" || user?.role === "superAdmin") && compliance.verificationStatus === 'pending' && compliance.isSendForVerification === true && (
                                    <button
                                      onClick={() => openVerify(compliance)}
                                      className="w-full text-left block rounded px-2 py-2  text-gray-600 "
                                    >
                                      <div className="flex items-center hover:bg-blue-100 rounded px-2 py-1">
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        Verify
                                      </div>
                                    </button>
                                  )}


                                  {user?.role === "admin" && (
                                    <>
                                      <button
                                        onClick={() => handleDelete(compliance._id)}
                                        className="w-full text-left block rounded px-2 py-2  text-gray-600 "
                                      >
                                        <div className="flex items-center hover:bg-blue-100 rounded px-2 py-1">
                                          <Trash2 className="h-4 w-4 mr-2" />
                                          Delete
                                        </div>
                                      </button>



                                    </>
                                  )}
                                </div>
                                </div>
                              )}
                            </div>
                          </td>




                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={user?.role === "admin" ? 10 : 9}
                          className="px-6 py-4 text-center text-gray-500"
                        >
                          No compliances found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>


              {totalFiltered > 0 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing{" "}
                        <span className="font-medium">
                          {totalFiltered === 0 ? 0 : (currentPage - 1) * pageSize + 1}
                        </span>{" "}
                        to{" "}
                        <span className="font-medium">
                          {Math.min(currentPage * pageSize, totalFiltered)}
                        </span>{" "}
                        of <span className="font-medium">{totalFiltered}</span>{" "}
                        results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(
                            (page) =>
                              page === 1 ||
                              page === totalPages ||
                              Math.abs(page - currentPage) <= 1
                          )
                          .map((page, index, array) => (
                            <React.Fragment key={page}>
                              {index > 0 && array[index - 1] !== page - 1 && (
                                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                  ...
                                </span>
                              )}
                              <button
                                onClick={() => setCurrentPage(page)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === currentPage
                                  ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                  : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                                  }`}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          ))}
                        <button
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage >= totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <SubmitVerificationModal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        onSubmitted={() => queryClient.invalidateQueries('compliances')}
        complianceId={activeCompliance?._id}
      />
      <VerifyModal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        onVerified={() => queryClient.invalidateQueries('compliances')}
        compliance={activeCompliance}
      />
      <HistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        complianceId={activeCompliance?._id}
      />
      <ConfirmModal
        isOpen={showConfirmDeleteSelected}
        onClose={() => setShowConfirmDeleteSelected(false)}
        onConfirm={confirmBulkDelete}
        title="Delete Selected Compliances"
        message={`Are you sure you want to delete ${selectedCompliances.length} selected compliance(s)? This action cannot be undone.`}
        confirmText="Delete"
      />
      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSubmit={handleImportSubmit}
        isLoading={isImporting}
      />
    </>
  );
};

export default ComplianceList;